// InsightEdit.js
import { useState } from 'react'
import {
    PencilIcon,
    PaletteIcon,
    FolderOpenIcon,
    TrashIcon,
    SparkleIcon,
    BugIcon,
} from '@phosphor-icons/react'
import { useDispatch, useSelector } from 'react-redux'
import { deleteInsight } from '../../../store/actions'
import { useModal, MODALS } from '../../modals'
import { loadFromSavedInsight } from './InsightAPI'
import { useNavigate, useParams } from 'react-router-dom'
import { ROUTES } from '../../../router/routes'
import { EVENTS, trackEvent } from '../../../events'
import { ScoopMenu, ScoopMenuItem, ScoopMenuDivider, ScoopButton } from '../../ui'
import styles from './InsightEdit.module.scss'

export default function InsightEdit({
    setIsMenuOpen,
    config,
    chartState,
    setConfig,
    isChartASkeleton,
    setIsChartASkeleton,
    server,
    title,
    id,
    insightKey,
    handleTitleChange,
    workspaceMetadata,
    insightPrompts,
}) {
    const [anchorEl, setAnchorEl] = useState(null)
    const [selectedItemToLoad, setSelectedItemToLoad] = useState(null)

    // Modal Manager hook
    const openModal = useModal(MODALS.COMMON.OPEN)
    const editElementModal = useModal(MODALS.CANVAS.EDIT_ELEMENT)
    const deleteModal = useModal(MODALS.COMMON.DELETE)

    // Get auth details from the state
    const userID = useSelector((state) => state.auth.userID)
    const workspaceID = useSelector((state) => state.auth.workspaceID)
    const dispatch = useDispatch()
    const open = Boolean(anchorEl)
    const navigate = useNavigate()
    const { canvasID } = useParams()

    const handleClick = (event) => {
        setIsMenuOpen(true)
        setAnchorEl(event.currentTarget)
    }

    const handleMenuClose = () => {
        setIsMenuOpen(false)
        setAnchorEl(null)
    }

    const handleDelete = () => {
        dispatch(deleteInsight(id))
        setIsMenuOpen(false)
        setAnchorEl(null)
    }

    const handleLoadFromSavedInsight = (insight) => {
        if (insight) {
            handleTitleChange({ target: { value: insight.insightName } })
            loadFromSavedInsight(
                insight,
                setConfig,
                chartState,
                setIsChartASkeleton,
                dispatch,
                id,
                selectedItemToLoad,
                workspaceID,
                insightPrompts,
                workspaceMetadata,
                title !== 'My Chart'
            )
            trackEvent(EVENTS.CANVAS.ADD_ASSET, {
                canvas: {
                    canvasID: canvasID,
                },
                insight: {
                    insightID: insight.insightKey,
                    title: insight.insightName,
                },
            })
        }
    }

    // gj: we may take it out later or need to expand on it, but it opens our B/E renderer to spotcheck charts
    const handleRenderDebug = (e) => {
        const baseURL = `https://embed.scoopanalytics.com/asset/chart?q=${userID}:${workspaceID}:${insightKey}:${canvasID}:screenshot=true:invite=Omega105-012:isDev=true`

        window.open(baseURL, '_blank')
    }

    // Defensive wrapper to prevent canvas event interference
    const createMenuItemHandler = (handler) => (event) => {
        event.stopPropagation() // Prevent canvas drag selection
        handler(event)
        setAnchorEl(null) // Close menu
    }

    return (
        <>
            <div>
                <ScoopButton
                    variant="text"
                    size="xxs"
                    onClick={handleClick}
                    aria-label="Open menu"
                    className={styles.editButton}
                >
                    <SparkleIcon size={24} weight="fill" className={styles.sparkleIcon} />
                </ScoopButton>
                <ScoopMenu
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handleMenuClose}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                >
                    <ScoopMenuItem
                        leftSection={<PaletteIcon size={18} weight="regular" />}
                        primary="Style"
                        onClick={createMenuItemHandler(() => {
                            editElementModal.show({ objectId: id })
                        })}
                    />
                    {!isChartASkeleton && (
                        <ScoopMenuItem
                            leftSection={<PencilIcon size={18} weight="regular" />}
                            primary="Edit"
                            onClick={createMenuItemHandler(() => {
                                navigate(ROUTES.EXPLORER, {
                                    state: {
                                        editmodeConfig: config,
                                        canvasID: canvasID,
                                        insightKey: insightKey,
                                        insightName: title,
                                    },
                                })
                            })}
                        />
                    )}
                    <ScoopMenuItem
                        leftSection={<FolderOpenIcon size={18} weight="regular" />}
                        primary="Load"
                        onClick={createMenuItemHandler(() => {
                            openModal.show({
                                setInsight: handleLoadFromSavedInsight,
                                workspaceMetadata,
                                server,
                                setSelectedItemToLoad,
                                setAnchor: setAnchorEl,
                            })
                        })}
                    />

                    {process.env.REACT_APP_SCOOP_ENV === 'dev' && (
                        <ScoopMenuItem
                            leftSection={<BugIcon size={18} weight="regular" />}
                            primary="Render"
                            onClick={createMenuItemHandler(handleRenderDebug)}
                        />
                    )}

                    <ScoopMenuDivider />
                    <ScoopMenuItem
                        leftSection={<TrashIcon size={18} weight="regular" />}
                        primary="Delete"
                        onClick={createMenuItemHandler(() => {
                            deleteModal.show({
                                title,
                                type: 'Insight',
                                onDelete: handleDelete,
                            })
                        })}
                    />
                </ScoopMenu>
            </div>
        </>
    )
}
